import { useCallback, useEffect, useRef, useState } from "react";
import AnchoredPopover from "./AnchoredPopover.jsx";
import { SEARCH_KINDS } from "../lib/traceSearch.js";
import "./TraceSearchBar.css";

// Find-in-trace for the Context reading: a keyword filter with a message-type
// filter behind the filter icon. The flyout and chips follow the Explore view's
// query bar; the field and icon button match the trace log's QueryBar.

const KIND = {
  user: { chip: "User", hint: "What a person typed" },
  assistant: { chip: "Assistant", hint: "Replies and thinking" },
  tool: { chip: "Tools", hint: "Tool calls and their results" },
  system: { chip: "System", hint: "System prompts, offered tools, and text the harness inserted" },
};
const Icon = ({ d }) => <svg className="trace-search-icon" viewBox="0 0 16 16" aria-hidden="true"><path d={d} /></svg>;
const FILTER = "M2 4h12M4 8h8M6.5 12h3";
const CLOSE = "m4 4 8 8m0-8-8 8";
const UP = "M4 10l4-4 4 4";
const DOWN = "M4 6l4 4 4-4";
// How long the pointer rests on the funnel before the filter opens, and how
// far it may drift while resting, so passing over it opens nothing.
const HOVER_REST_MS = 150, HOVER_REST_SLOP = 4;

export default function TraceSearchBar({ inputRef, input, onInput, onKeyDown, onClearKeyword, result, hits, activeHitId, onStep, kinds, counts, onToggleKind, onClearKinds }) {
  const funnel = useRef(null);
  const [flyout, setFlyout] = useState(null); // { pinned }
  const hoverTimer = useRef(0);
  const resting = useRef(null);
  const inside = useRef(false);
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  const close = useCallback((refocus) => {
    clearTimeout(hoverTimer.current);
    setFlyout(null);
    if (refocus) funnel.current?.focus();
  }, []);
  const open = () => {
    resting.current = null;
    clearTimeout(hoverTimer.current);
    setFlyout({ pinned: true });
  };
  const onPointer = (entering) => {
    inside.current = entering;
    clearTimeout(hoverTimer.current);
    if (!entering && flyout && !flyout.pinned) hoverTimer.current = setTimeout(() => { if (!inside.current) setFlyout(null); }, 300);
  };
  const settle = (event) => {
    clearTimeout(hoverTimer.current);
    resting.current = { x: event.clientX, y: event.clientY };
    hoverTimer.current = setTimeout(() => { resting.current = null; setFlyout((current) => current ?? { pinned: false }); }, HOVER_REST_MS);
  };

  const at = hits.findIndex((h) => h.id === activeHitId);
  const total = `${hits.length.toLocaleString("en-US")}${result?.capped ? "+" : ""}`;
  const counter = hits.length ? `${at < 0 ? 0 : at + 1} of ${total}` : "No matches";
  // One field, as the trace log's QueryBar: the filter cell, the chips, the
  // keyword, then its counter, steppers and clear at the end.
  return (
    <div className="trace-search" data-trace-search="" role="search">
      <button
        ref={funnel}
        type="button"
        className="trace-search-funnel"
        aria-label="Filter by message type"
        aria-haspopup="dialog"
        aria-expanded={Boolean(flyout)}
        onClick={() => (flyout?.pinned ? close(false) : open())}
        onMouseEnter={(event) => { inside.current = true; clearTimeout(hoverTimer.current); resting.current = null; if (!flyout) settle(event); }}
        onMouseMove={(event) => { if (resting.current && Math.hypot(event.clientX - resting.current.x, event.clientY - resting.current.y) > HOVER_REST_SLOP) settle(event); }}
        onMouseLeave={() => { resting.current = null; onPointer(false); }}
        onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); open(); } }}
      >
        <Icon d={FILTER} />
      </button>
      {kinds.length ? (
        <div className="trace-search-chips">
          {kinds.map((kind) => (
            <span key={kind} className="trace-search-chip" data-search-chip={kind}>
              <button type="button" className="trace-search-chip-label" onClick={open}>{KIND[kind].chip}</button>
              <button type="button" className="trace-search-chip-remove" aria-label={`Remove ${KIND[kind].chip} filter`} onClick={() => onToggleKind(kind)}><Icon d={CLOSE} /></button>
            </span>
          ))}
        </div>
      ) : null}
      {/* Plain text, not type="search": the browser's own clear button sits
          straight after the text, ahead of the counter. */}
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Filter messages"
        aria-label="Filter messages by keyword"
      />
      {result ? <span className="trace-search-count" data-search-count="" aria-live="polite">{counter}</span> : null}
      {result ? (
        <>
          <button type="button" className="trace-search-step" aria-label="Previous match" disabled={!hits.length} onClick={() => onStep(-1)}><Icon d={UP} /></button>
          <button type="button" className="trace-search-step" aria-label="Next match" disabled={!hits.length} onClick={() => onStep(1)}><Icon d={DOWN} /></button>
        </>
      ) : null}
      {input ? (
        <button type="button" className="trace-search-step" aria-label="Clear keyword" title="Clear keyword" onClick={() => { onClearKeyword(); inputRef.current?.focus(); }}><Icon d={CLOSE} /></button>
      ) : null}
      {flyout ? (
        <AnchoredPopover anchor={funnel} onClose={close} label="Filter by message type" className="trace-search-flyout" onMouseEnter={() => onPointer(true)} onMouseLeave={() => onPointer(false)}>
          <p className="trace-search-flyout-title">Message type</p>
          <div className="trace-search-values" role="group" aria-label="Message type">
            <button type="button" className="trace-search-value is-any" aria-pressed={!kinds.length} onClick={() => { onClearKinds(); close(false); }}><span>Any</span></button>
            {SEARCH_KINDS.map((kind) => (
              <button key={kind} type="button" className="trace-search-value" data-search-kind={kind} aria-pressed={kinds.includes(kind)} onClick={() => { onToggleKind(kind); close(false); }}>
                <span>{KIND[kind].chip}<small>{KIND[kind].hint}</small></span>
                <b>{(counts[kind] ?? 0).toLocaleString("en-US")}</b>
              </button>
            ))}
          </div>
          <p className="trace-search-flyout-note">Counts are loaded messages.</p>
        </AnchoredPopover>
      ) : null}
    </div>
  );
}
